import { useContext, useMemo } from 'react'
import moment from 'moment'

import { groupBy, sortBy } from 'lib/array/arrayUtils'
import useBedbankRates from 'hooks/useBedbankRates'
import { useBedbankRoomRatePromotions } from 'hooks/OfferPage/useBedbankPromotions'
import { isReservationAvailableForRoom } from 'lib/checkout/cartReservationUtils'
import { getPromotionMinLos, getBedbankCalendar, isFlashCalendarEnabled } from 'selectors/bedbankOfferSelectors'
import GeoContext from 'contexts/geoContext'
import getBedbankPromoExtraValue from 'lib/hotels/bedbankUtils'
import { useAppSelector } from './reduxHooks'

function useBedbankPackageViews(
  offer: App.BedbankOffer,
  region: string,
  currency: string,
  rooms: Array<App.Occupants>,
  duration: number,
  checkIn?: moment.Moment,
  checkOut?: moment.Moment,
): [App.BedbankOfferPackageView[], boolean] {
  const [hotelRates, fetchingRates] = useBedbankRates(offer.id, rooms, checkIn, checkOut)

  const promotionMinLos = useAppSelector((_state) => getPromotionMinLos({ bedbankOffer: offer }))
  const calendarData = useAppSelector((state) => getBedbankCalendar({ bedbankOffer: offer }, state))
  const isCalendarEnabled = useAppSelector((state) => isFlashCalendarEnabled({ bedbankOffer: offer }, state))

  const hasPromotions = offer.promotions.length > 0
  const promotions = useBedbankRoomRatePromotions(offer.promotions, checkIn, checkOut)
  const hasValidPromotions = Object.values(promotions).length
  const validCheckinCheckoutDates = !!(checkIn && checkOut)

  const { showTaxExclusivePrices } = useContext(GeoContext)

  const result = useMemo(() => {
    const ratesGroup = groupBy(hotelRates, (rate) => rate.roomId)
    const packageViews = offer.packages.map(pkg => {
      let sortedRates = sortBy(hotelRates.filter(rate => rate.roomId === pkg.roomId), rate => rate.totals.inclusive, 'asc')
      const promotionsRates = sortedRates.filter(rate => promotions[rate.id]?.id === pkg.promotionId).reduce((acc, rate) => {
        acc[rate.id] = promotions[rate.id]
        return acc
      }, {})

      let show = false

      const showAllRates = promotionMinLos > duration && isCalendarEnabled || false

      const showOnlyPromo = hasPromotions && !validCheckinCheckoutDates

      const overrideValueAtPrice = hasPromotions && validCheckinCheckoutDates

      if (overrideValueAtPrice) {
        sortedRates = sortedRates.map((rate) => {
          if (promotions[rate.id]) {
            const inclusionsAmount = pkg.sell ? getBedbankPromoExtraValue({
              price: pkg.sell.price,
              value: pkg.sell.value,
              minLos: pkg.sell.los,
              duration,
            }) : 0
            const displayedHotelValue = inclusionsAmount + (showTaxExclusivePrices ? rate.totals.exclusive : rate.totals.inclusive)

            return {
              ...rate,
              value: Math.round(displayedHotelValue),
              discount: Number((inclusionsAmount / displayedHotelValue).toFixed(2)),
            }
          } else return rate
        })
      }

      if (showOnlyPromo && showAllRates) {
        show = true
      } else if (hasPromotions) {
        const promo = offer.promotions.find((p) => p.id === pkg.promotionId)
        if (!validCheckinCheckoutDates && promo) {
          if ((isCalendarEnabled && calendarData?.rooms?.some(x => x.id === pkg.roomId)) || !isCalendarEnabled) {
            show = true
          }
        } else if (validCheckinCheckoutDates && promo && hasValidPromotions) {
          show = true
        } else if (validCheckinCheckoutDates && !hasValidPromotions) {
          show = true
        }
      } else {
        show = true
      }

      if (showOnlyPromo && !showAllRates) {
        const promo = offer.promotions.find((p) => p.id === pkg.promotionId)
        if (promo && pkg.sell) {
          const price = Math.round((pkg.sell.price / pkg.sell.los) * duration)
          const value = Math.round((pkg.sell.value / pkg.sell.los) * duration)
          const discount = Number(((pkg.sell.value - pkg.sell.price) / pkg.sell.value).toFixed(2))
          sortedRates.push({
            id: promo.id,
            bedGroups: [],
            facilities: [],
            regionCode: region,
            roomId: pkg.roomId,
            occupancyPricing: [],
            totals: {
              exclusive: price,
              inclusive: price,
              taxesAndFees: 0,
              propertyFees: 0,
            },
            value,
            nights: duration,
            discount: discount > 0.02 ? discount : 0,
            refundable: false,
            partiallyRefundable: false,
            isFlightBundle: false,
            corporate: false,
            promotions: [],
            cancellationPolicies: [],
            optionId: promo.id,
            currencyCode: currency,
            nonRefundableDateRanges: [],
            cancellationPolicy: {
              policies: [],
              description: ['If you cancel your booking, you are not eligible for a refund or credit for a future stay.'],
            },
          })

          promotionsRates[promo.id] = promo
        }
      }

      return {
        package: pkg,
        available: validCheckinCheckoutDates && ratesGroup.has(pkg.roomId) || !validCheckinCheckoutDates,
        capacityValid: rooms.every(room => isReservationAvailableForRoom(room, pkg.capacities.combinations, offer)),
        rates: sortedRates,
        cheapestRate: sortedRates[0],
        promotions: promotionsRates,
        show,
      }
    })
    return packageViews
  }, [hotelRates, offer, promotionMinLos, duration, isCalendarEnabled, hasPromotions, validCheckinCheckoutDates, rooms, promotions, hasValidPromotions, calendarData?.rooms, region, currency, showTaxExclusivePrices])

  return [result, fetchingRates]
}

export default useBedbankPackageViews
